import { useEffect, useState } from "react";
import { Copy, Pencil, Plus } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Button } from "./ui/button";

const EMPTY_NUTRIENTS = {
  calories: 0,
  protein: 0,
  carbs: 0,
  sugars: 0,
  fat: 0,
  saturated_fat: 0,
  fiber: 0,
  sodium_mg: 0,
};

const MACRO_FIELDS = [
  { key: "calories", label: "Calories (kcal)", step: "1" },
  { key: "protein", label: "Protéines (g)", step: "0.1" },
  { key: "carbs", label: "Glucides (g)", step: "0.1" },
  { key: "sugars", label: "dont sucres (g)", step: "0.1" },
  { key: "fat", label: "Lipides (g)", step: "0.1" },
  { key: "saturated_fat", label: "AG saturés (g)", step: "0.1" },
  { key: "fiber", label: "Fibres (g)", step: "0.1" },
  { key: "sodium_mg", label: "Sodium (mg)", step: "1" },
];

/**
 * Dialog for editing or duplicating a library entry.
 * mode: "edit" | "duplicate" | "create"
 */
export const LibraryEntryDialog = ({
  open,
  onOpenChange,
  mode,
  entry,
  onSave,
}) => {
  const [nameFr, setNameFr] = useState("");
  const [nutrients, setNutrients] = useState(EMPTY_NUTRIENTS);

  useEffect(() => {
    if (!open) return;
    if (mode === "create") {
      setNameFr("");
      setNutrients({ ...EMPTY_NUTRIENTS });
    } else if (entry) {
      setNameFr(mode === "duplicate" ? `${entry.name_fr} (copie)` : entry.name_fr);
      setNutrients({ ...EMPTY_NUTRIENTS, ...(entry.nutrients_per_100g || {}) });
    }
  }, [open, mode, entry]);

  const setMacro = (key, value) =>
    setNutrients((n) => ({ ...n, [key]: parseFloat(value) || 0 }));

  const canSubmit = nameFr.trim().length > 0;

  const handleSave = async () => {
    if (!canSubmit) return;
    await onSave({
      name_fr: nameFr.trim(),
      nutrients_per_100g: nutrients,
    });
    onOpenChange(false);
  };

  const titleIcon =
    mode === "duplicate" ? Copy : mode === "create" ? Plus : Pencil;
  const TitleIcon = titleIcon;
  const titleText =
    mode === "duplicate"
      ? "Dupliquer l'ingrédient"
      : mode === "create"
      ? "Nouvel ingrédient personnalisé"
      : "Modifier l'ingrédient";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-xl p-0 overflow-hidden gap-0"
        data-testid="library-entry-dialog"
      >
        <DialogHeader className="px-6 pt-5 pb-3 border-b border-stone-100">
          <DialogTitle className="font-heading text-lg flex items-center gap-2">
            <TitleIcon className="h-5 w-5 text-emerald-700" />
            {titleText}
          </DialogTitle>
          <DialogDescription className="text-xs text-stone-500">
            {mode === "duplicate"
              ? "Ajustez le nom et les macros pour créer une variante."
              : "Valeurs nutritionnelles pour 100 g."}
          </DialogDescription>
        </DialogHeader>

        <div className="px-6 py-5 space-y-4 max-h-[60vh] overflow-y-auto scrollbar-thin">
          <div>
            <label className="block text-[10px] tracking-[0.2em] uppercase font-bold text-stone-500 mb-1.5">
              Nom (français) <span className="text-red-500 normal-case">*</span>
            </label>
            <Input
              value={nameFr}
              onChange={(e) => setNameFr(e.target.value)}
              placeholder="Ex : Pâte de curry rouge maison"
              className="focus-visible:ring-emerald-500"
              data-testid="library-name-input"
              autoFocus
            />
          </div>

          <div className="pt-2 border-t border-stone-100">
            <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-stone-500 mb-3">
              Valeurs nutritionnelles pour 100 g
            </div>
            <div className="grid grid-cols-2 gap-3">
              {MACRO_FIELDS.map((f) => (
                <div key={f.key}>
                  <label className="block text-[11px] text-stone-600 mb-1">{f.label}</label>
                  <Input
                    type="number"
                    min="0"
                    step={f.step}
                    value={nutrients[f.key]}
                    onChange={(e) => setMacro(f.key, e.target.value)}
                    className="h-9 focus-visible:ring-emerald-500"
                    data-testid={`library-macro-${f.key}`}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter className="px-6 py-3 border-t border-stone-100 bg-stone-50/40">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="text-stone-600"
            data-testid="library-cancel"
          >
            Annuler
          </Button>
          <Button
            onClick={handleSave}
            disabled={!canSubmit}
            className="bg-emerald-700 hover:bg-emerald-800 text-white"
            data-testid="library-save"
          >
            Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default LibraryEntryDialog;
